import type { Auth0ContextInterface } from "@auth0/auth0-react";
import type {
  UserPreferencesInputBodyLanguage,
  UserProfile,
} from "@partners/ui/api";

import { useAuth0 } from "@auth0/auth0-react";
import { LaunchDarklyProvider, Loader } from "@partners/ui";
import { useGetUser, usePatchUserPreferences } from "@partners/ui/api";
import { FetchClientProvider } from "@partners/ui/fetch-client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useSegmentAnalytics } from "./hooks/analytics/useSegmentAnalytics";
import { usePortalFetchClient } from "./hooks/usePortalFetchClient";
import { getSuggestedLanguage } from "./i18n/getSuggestedLanguage";
import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-router" {
  type MyRegister = {
    router: typeof router;
  };
}

export const router = createRouter({
  routeTree,
  context: { auth: undefined! },
});

const PEPPERSTONE_CLAIMS_URL = "https://secure.pepperstone.com/customClaims";

const getLDUserContext = (authUser: ReturnType<typeof useAuth0>["user"]) => {
  const userId = authUser?.[PEPPERSTONE_CLAIMS_URL]?.userId?.toString();
  const email = authUser?.email;
  return { userId, email };
};

export const App = () => {
  const fetchClient = usePortalFetchClient();
  return (
    <FetchClientProvider value={fetchClient}>
      <InnerApp />
    </FetchClientProvider>
  );
};

const InnerApp = () => {
  const auth = useAuth0();
  const { i18n } = useTranslation();

  const { identifyUser } = useSegmentAnalytics();
  const { data: user, isLoading: isUserLoading } = useGetUser(undefined, {
    query: { enabled: !auth.isLoading && auth.isAuthenticated },
  });
  const { mutate } = usePatchUserPreferences({ mutation: { retry: 3 } });
  const scaUserIDRef = useRef<string | undefined>(undefined);
  const langSyncedRef = useRef(false);

  useLayoutEffect(() => {
    document.dir = i18n.dir();
  }, [i18n, i18n.language]);

  // Determines the active language once per session, in priority order:
  //   1. URL param "lng"          — explicit link (e.g. IB signup ?lng=vi); picked up
  //                                 by i18next LanguageDetector before this effect runs,
  //                                 so we simply skip the server override and let it stand.
  //   2. User preferred language  — stored server-side; applied here when no URL param.
  //   3. localStorage language    — i18next LanguageDetector reads this on init;
  //                                 if already set, skip the geo fallback below.
  //   4. Viewer country suggestion — geo-based fallback for new users with no stored
  //                                 preference (e.g. viewerCountry "TH" → "th").
  //
  // A ref guards against re-running on background refetches, which could revert
  // an in-flight language change before the mutate has persisted to the server.
  // Effect 2 persists whatever language is active back to the server, so all
  // sources converge on the next session.
  useEffect(() => {
    if (isUserLoading || !user || langSyncedRef.current) return;
    langSyncedRef.current = true;

    // Priority 1: URL param already active — do not override.
    const urlLng = new URL(window.location.href).searchParams.get("lng");
    if (urlLng) return;

    // Priority 2: Server-stored preference (return even when it matches current
    // language so we don't fall through to the geo fallback below).
    const preferred = user.preferences?.language;
    if (preferred) {
      if (preferred !== i18n.language) {
        i18n.changeLanguage(preferred);
      }
      return;
    }

    // Priority 3: localStorage already handled by i18next LanguageDetector on init.
    if (localStorage.getItem("i18nextLng")) return;

    // Priority 4: Geo-based suggestion for new users with no stored preference.
    if (user.viewerCountry) {
      const suggested = getSuggestedLanguage(user.viewerCountry);
      if (suggested !== i18n.language) {
        i18n.changeLanguage(suggested);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoading, user]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("lng", i18n.language);
    window.history.replaceState(window.history.state, "", url.toString());
    if (user && user.preferences?.language !== i18n.language) {
      mutate({
        data: { language: i18n.language as UserPreferencesInputBodyLanguage },
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const { userId, email } = getLDUserContext(auth.user);

  useEffect(() => {
    if (userId && userId !== scaUserIDRef.current) {
      scaUserIDRef.current = userId;
      identifyUser(userId, { email });
    }
  }, [identifyUser, userId, email]);

  if (auth.isLoading || isUserLoading) {
    return <Loader />;
  }

  return (
    <LaunchDarklyProvider
      clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID}
      userId={userId}
      email={email}
      application="portal"
    >
      <AppRouter auth={auth} user={user} />
    </LaunchDarklyProvider>
  );
};

const AppRouter = ({
  auth,
  user,
}: {
  auth: Auth0ContextInterface;
  user: UserProfile | undefined;
}) => {
  const ldClient = useLDClient();

  const { userId, email } = getLDUserContext(auth.user);
  const country = user?.country?.toLowerCase();

  useEffect(() => {
    ldClient?.identify(
      {
        kind: "user",
        key: userId ?? email,
        userId,
        email,
        country,
        application: "portal",
      },
      undefined,
      () => {
        console.log("New context's flags available");
      },
    );
  }, [country, email, ldClient, userId]);

  return <RouterProvider router={router} context={{ auth }} />;
};
